/**
 * /forensic-enhancer — engine: the picture pipeline and the video source.
 *
 *   source  (the frame as captured, or the mean/median of N frames)
 *     → geometry   deinterlace → undistort → rotate → pixel aspect → [perspective, crop]…
 *     → base       (what "before" means in the compare view)
 *     → enhance    deblur → denoise → tone → levels → CLAHE → hist-eq → colour → sharpen → edge → invert
 *     → current    (what is shown, exported and reported)
 *
 * Everything per-pixel runs in forensic-worker.js. This file owns state, the
 * order of operations (the order is the forensic order, see Amped's "filter
 * sequence" note: undo distortions in the reverse order they happened), the
 * undo history, and the video source — frame-exact stepping through <video>
 * plus a keyframe-seeking WebCodecs decoder for "the N frames around this one",
 * which is what the sharpness search and frame stacking are built on.
 *
 * Nothing here touches the DOM except the hidden <video>. app.js renders.
 */
(function () {
'use strict';
if (window.FX) return;

var T = window.FX_I18N || {};
var CFG = window.FX_CFG || {};

/* ───────────────────────── state ───────────────────────── */
var S = {
    kind: null,            // 'video' | 'image'
    file: null,
    limitKey: null,        // stamped once per file: the daily-cap unit is the FILE
    video: { el: null, loaded: false, duration: 0, fps: 30, fpsExact: false, w: 0, h: 0, codec: '', nbFrames: 0, mp4: null, bitrate: 0 },
    source: null,          // ImageData
    sourceMeta: { time: 0, frame: 0, stacked: null },
    base: null,            // ImageData after geometry
    current: null,         // ImageData after enhancement (strength applied at render)
    aiResult: null,        // { img: ImageData, scale } — shown INSTEAD of current while present
    geom: defaultGeom(),
    enh: defaultEnh(),
    combScore: null,
    captured: [],          // [{ id, bmp, w, h, time, frame, selected, thumb, best }]
    history: [], future: [],
    busy: 0,
};
function defaultGeom() { return { deint: 'off', k1: 0, rotate: 0, par: 1, actions: [] }; }
function defaultEnh() {
    return {
        strength: 1,
        steps: {
            deblur:   { on: false, angle: 0, len: 9, noise: 0.01 },
            optical:  { on: false, sigma: 1.2, iters: 10 },
            denoise:  { on: false, strength: 6 },
            tone:     { on: false, brightness: 0, contrast: 0, gamma: 1, saturation: 0 },
            levels:   { on: false, black: 0, white: 255, gamma: 1 },
            clahe:    { on: false, clip: 3, tile: 8 },
            histeq:   { on: false, mode: 'luma' },
            colorbal: { on: false, sr: 0, sg: 0, sb: 0, mr: 0, mg: 0, mb: 0, hr: 0, hg: 0, hb: 0 },
            sharpen:  { on: false, radius: 2, amount: 1.5 },
            edge:     { on: false, amount: 1 },
            invert:   { on: false },
        }
    };
}
/* The chain order is fixed and is the forensic order: remove the blur first
   (it is a property of the capture) — the smear of motion, then the softness
   of the lens and the codec — then the noise, then stretch tone, then sharpen
   what is left. Sharpening before deblurring sharpens the smear. */
var CHAIN_ORDER = ['deblur', 'optical', 'denoise', 'tone', 'levels', 'clahe', 'histeq', 'colorbal', 'sharpen', 'edge', 'invert'];

/* ───────────────────────── worker bridge ───────────────────────── */
var worker = null, reqId = 0, pending = new Map();
function ensureWorker() {
    if (worker) return worker;
    worker = new Worker(CFG.worker || '/js/forensic-worker.js');
    worker.onmessage = function (e) {
        var m = e.data; if (!m || m.id == null) return;
        var p = pending.get(m.id); if (!p) return;
        if (m.type === 'progress') { if (p.onProgress) p.onProgress(m.percent / 100); return; }
        pending.delete(m.id);
        if (m.type === 'error') { var err = new Error(m.message || m.error || 'WORKER_FAILED'); err.code = m.code || m.error; p.reject(err); }
        else p.resolve(m);
    };
    worker.onerror = function (e) {
        // A crashed worker is replaced on the next call; every waiter is told.
        pending.forEach(function (p) { p.reject(new Error('WORKER_CRASHED')); });
        pending.clear();
        try { worker.terminate(); } catch (_) {}
        worker = null;
        try { console.error('forensic-worker', e); } catch (_) {}
    };
    return worker;
}
function work(msg, transfer, onProgress) {
    return new Promise(function (resolve, reject) {
        var w = ensureWorker();
        msg.id = ++reqId;
        pending.set(msg.id, { resolve: resolve, reject: reject, onProgress: onProgress });
        if (transfer && transfer.length) w.postMessage(msg, transfer); else w.postMessage(msg);
    });
}
function toImageData(m) { return new ImageData(new Uint8ClampedArray(m.data), m.width, m.height); }
function cloneImageData(img) { return new ImageData(new Uint8ClampedArray(img.data), img.width, img.height); }

/* ───────────────────────── busy + events ───────────────────────── */
var listeners = {};
function on(ev, fn) { (listeners[ev] = listeners[ev] || []).push(fn); }
function emit(ev, a) { (listeners[ev] || []).forEach(function (fn) { try { fn(a); } catch (e) { console.error(e); } }); }
function busy(delta, text) { S.busy = Math.max(0, S.busy + delta); emit('busy', { n: S.busy, text: text }); }

/* ───────────────────────── canvas helpers ───────────────────────── */
function imageDataToCanvas(img) {
    var c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
    c.getContext('2d').putImageData(img, 0, 0); return c;
}
function drawableToImageData(src, w, h) {
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    var x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(src, 0, 0, w, h);
    return x.getImageData(0, 0, w, h);
}
function stretchImageData(img, ratio) {
    var outW = Math.max(2, Math.round(img.height * ratio));
    if (outW === img.width) return img;
    var src = imageDataToCanvas(img);
    var c = document.createElement('canvas'); c.width = outW; c.height = img.height;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    x.drawImage(src, 0, 0, outW, img.height);
    return x.getImageData(0, 0, outW, img.height);
}
function cropImageData(img, r) {
    var x0 = Math.max(0, Math.round(r.x)), y0 = Math.max(0, Math.round(r.y));
    var w = Math.min(img.width - x0, Math.round(r.w)), h = Math.min(img.height - y0, Math.round(r.h));
    if (w < 2 || h < 2) return img;
    var out = new Uint8ClampedArray(w * h * 4);
    for (var y = 0; y < h; y++) out.set(img.data.subarray(((y0 + y) * img.width + x0) * 4, ((y0 + y) * img.width + x0 + w) * 4), y * w * 4);
    return new ImageData(out, w, h);
}
function downscaleImageData(img, maxW) {
    if (img.width <= maxW) return img;
    var f = maxW / img.width;
    return drawableToImageData(imageDataToCanvas(img), Math.round(img.width * f), Math.round(img.height * f));
}

/* ───────────────────────── geometry ───────────────────────── */
/* Rebuild base from source through the geometry chain. Settings first in the
   canonical order, then the actions (perspective / crop) in the order taken —
   each action's coordinates live in the space the previous step produced. */
var geomToken = 0;
function rebuildBase() {
    var my = ++geomToken;
    if (!S.source) return Promise.resolve();
    busy(1, T.busy_geom);
    var g = S.geom;
    var p = Promise.resolve(cloneImageData(S.source));
    if (g.deint !== 'off') p = p.then(function (img) { return work({ type: 'pipeline', imageData: img, params: { filters: [{ name: 'deinterlace', enabled: true, params: { mode: g.deint } }] } }, [img.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    if (g.k1) p = p.then(function (img) { return work({ type: 'pipeline', imageData: img, params: { filters: [{ name: 'undistort', enabled: true, params: { k1: g.k1 } }] } }, [img.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    if (g.rotate) p = p.then(function (img) { return work({ type: 'rotate', imageData: img, quarter: g.rotate }, [img.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    if (g.par !== 1) p = p.then(function (img) { return stretchImageData(img, g.par); });
    g.actions.forEach(function (a) {
        if (a.type === 'crop') p = p.then(function (img) { return cropImageData(img, a.rect); });
        else if (a.type === 'persp') p = p.then(function (img) {
            return work({ type: 'warp', imageData: img, quad: a.quad, outWidth: a.outW, outHeight: a.outH }, [img.data.buffer]).then(function (m) { return toImageData(m.imageData); });
        });
    });
    return p.then(function (img) {
        if (my !== geomToken) return;           // a newer rebuild superseded this one
        S.base = img;
        S.aiResult = null;                       // AI pixels belong to the geometry they were built on
        emit('base');
        return applyEnhance();
    }).catch(function (err) { emit('error', err); }).then(function () { busy(-1); });
}
function setGeomSetting(key, value) {
    if (S.geom[key] === value) return Promise.resolve();
    pushHistory();
    S.geom[key] = value;
    // An action was taken in a space this setting has just changed; its
    // coordinates no longer mean the same thing. Drop them and say so.
    if (S.geom.actions.length) { S.geom.actions = []; emit('actions_dropped'); }
    dropRulerIf();
    return rebuildBase();
}
function addGeomAction(action) {
    pushHistory();
    S.geom.actions.push(action);
    dropRulerIf();
    return rebuildBase();
}
function resetGeometry() {
    pushHistory();
    S.geom = defaultGeom();
    dropRulerIf();
    return rebuildBase();
}
function dropRulerIf() { if (S.rulerScale) { S.rulerScale = null; emit('ruler_dropped'); } }

/* ───────────────────────── enhancement ───────────────────────── */
function chainFilters(enh) {
    var st = enh.steps, f = [];
    CHAIN_ORDER.forEach(function (k) {
        var s = st[k]; if (!s || !s.on) return;
        switch (k) {
            case 'deblur':  f.push({ name: 'deblur', enabled: true, params: { angle: s.angle, size: s.len | 1, noise: s.noise } }); break;
            case 'optical': f.push({ name: 'optical', enabled: true, params: { sigma: s.sigma, iters: s.iters | 0 } }); break;
            case 'denoise': f.push({ name: 'denoise', enabled: true, params: { strength: s.strength } }); break;
            case 'tone':
                if (s.brightness) f.push({ name: 'brightness', enabled: true, params: { value: s.brightness } });
                if (s.contrast) f.push({ name: 'contrast', enabled: true, params: { value: s.contrast } });
                if (s.gamma !== 1) f.push({ name: 'gamma', enabled: true, params: { value: s.gamma } });
                if (s.saturation) f.push({ name: 'saturation', enabled: true, params: { value: s.saturation } });
                break;
            case 'levels':  if (s.black || s.white !== 255 || s.gamma !== 1) f.push({ name: 'levels', enabled: true, params: { black: s.black, white: s.white, gamma: s.gamma } }); break;
            case 'clahe':   f.push({ name: 'clahe', enabled: true, params: { clipLimit: s.clip, tileSize: s.tile } }); break;
            case 'histeq':  f.push({ name: 'histogramEq', enabled: true, params: { mode: s.mode } }); break;
            case 'colorbal': f.push({ name: 'colorBalance', enabled: true, params: { shadows: { r: s.sr, g: s.sg, b: s.sb }, midtones: { r: s.mr, g: s.mg, b: s.mb }, highlights: { r: s.hr, g: s.hg, b: s.hb } } }); break;
            case 'sharpen': f.push({ name: 'unsharp', enabled: true, params: { radius: s.radius, amount: s.amount } }); break;
            case 'edge':    f.push({ name: 'edge', enabled: true, params: { amount: s.amount } }); break;
            case 'invert':  f.push({ name: 'invert', enabled: true, params: {} }); break;
        }
    });
    return f;
}
/* Run the chain on any ImageData (the live base, or a captured frame in batch). */
function runChain(img, enh) {
    var filters = chainFilters(enh);
    if (!filters.length) return Promise.resolve(cloneImageData(img));
    var copy = cloneImageData(img);
    return work({ type: 'pipeline', imageData: copy, params: { filters: filters } }, [copy.data.buffer]).then(function (m) { return toImageData(m.imageData); });
}
/* Live apply: coalesced. While one run is in flight the latest request waits;
   dragging a slider never queues more than one extra run. */
var enhRunning = false, enhDirty = false;
function applyEnhance() {
    if (!S.base) return Promise.resolve();
    if (enhRunning) { enhDirty = true; return Promise.resolve(); }
    enhRunning = true;
    busy(1, T.busy_enhance);
    return runChain(S.base, S.enh).then(function (out) {
        S.current = out;
        S.enhRuns = (S.enhRuns | 0) + 1;      // a monotonic "the chain finished" counter, for tests and telemetry
        emit('current');
    }).catch(function (err) { emit('error', err); }).then(function () {
        busy(-1); enhRunning = false;
        if (enhDirty) { enhDirty = false; return applyEnhance(); }
    });
}
var enhDebounce = null;
function scheduleEnhance(ms) {
    clearTimeout(enhDebounce);
    enhDebounce = setTimeout(function () { S.aiResult = null; applyEnhance(); }, ms == null ? 60 : ms);
}
function setStep(key, patch, opts) {
    var s = S.enh.steps[key]; if (!s) return;
    if (!opts || !opts.noHistory) pushHistory();
    Object.keys(patch).forEach(function (k) { s[k] = patch[k]; });
    emit('enh');
    scheduleEnhance(opts && opts.now ? 0 : 60);
}
function setStrength(v) { S.enh.strength = Math.max(0, Math.min(1, v)); emit('current'); }
function resetEnhance() { pushHistory(); S.enh = defaultEnh(); emit('enh'); scheduleEnhance(0); }
function applyPreset(name) {
    pushHistory();
    var e = defaultEnh(), st = e.steps;
    switch (name) {
        case 'plate':
            st.denoise = { on: true, strength: 6 };
            st.tone = { on: true, brightness: 0, contrast: 25, gamma: 0.8, saturation: -100 };
            st.clahe = { on: true, clip: 5, tile: 8 };
            st.sharpen = { on: true, radius: 2, amount: 2.2 };
            break;
        case 'night':
            st.denoise = { on: true, strength: 14 };
            st.levels = { on: true, black: 0, white: 170, gamma: 1.6 };
            st.clahe = { on: true, clip: 6, tile: 8 };
            break;
        case 'cctv':
            st.denoise = { on: true, strength: 9 };
            st.tone = { on: true, brightness: 6, contrast: 12, gamma: 0.9, saturation: 0 };
            st.clahe = { on: true, clip: 4, tile: 8 };
            st.sharpen = { on: true, radius: 2, amount: 1.8 };
            break;
        case 'face':
            st.denoise = { on: true, strength: 10 };
            st.tone = { on: true, brightness: 0, contrast: 8, gamma: 1.05, saturation: 8 };
            st.clahe = { on: true, clip: 2.5, tile: 8 };
            st.sharpen = { on: true, radius: 1, amount: 1.2 };
            break;
        case 'text':
            st.tone = { on: true, brightness: 0, contrast: 30, gamma: 0.9, saturation: -100 };
            st.levels = { on: true, black: 40, white: 215, gamma: 1 };
            st.sharpen = { on: true, radius: 1, amount: 1.6 };
            break;
        default: break;
    }
    S.enh = e; emit('enh'); scheduleEnhance(0);
}

/* Blend base and current by strength (VideoCleaner's "enhancement
   transparency"): the honest slider between what the camera recorded and
   what the chain made of it. */
function composite() {
    if (S.aiResult) return S.aiResult.img;
    if (!S.current) return S.base;
    var s = S.enh.strength;
    if (s >= 0.999 || !S.base || S.base.width !== S.current.width || S.base.height !== S.current.height) return S.current;
    var a = S.base.data, b = S.current.data, out = new Uint8ClampedArray(b.length), inv = 1 - s;
    for (var i = 0; i < b.length; i += 4) {
        out[i] = a[i] * inv + b[i] * s; out[i + 1] = a[i + 1] * inv + b[i + 1] * s; out[i + 2] = a[i + 2] * inv + b[i + 2] * s; out[i + 3] = 255;
    }
    return new ImageData(out, S.current.width, S.current.height);
}

/* ───────────────────────── deblur search ───────────────────────── */
function searchDeblur(roi) {
    if (!S.base) return Promise.reject(new Error('NO_BASE'));
    var img = S.base;
    if (roi && roi.w > 16 && roi.h > 16) img = cropImageData(img, roi);
    img = downscaleImageData(img, 320);
    if (img.height > 320) img = drawableToImageData(imageDataToCanvas(img), Math.round(img.width * 320 / img.height), 320);
    var angles = []; for (var a = 0; a < 180; a += 5) angles.push(a);
    var st = S.enh.steps.deblur;
    busy(1, T.st_deblur_searching);
    return work({ type: 'deblurSearch', imageData: cloneImageData(img), angles: angles, sizes: [Math.max(5, (st.len | 1) - 4), st.len | 1, Math.min(31, (st.len | 1) + 6)], noise: st.noise }, null, function (p) { emit('progress', { key: 'deblur', p: p }); })
        .then(function (m) { busy(-1); return m.best; }, function (e) { busy(-1); throw e; });
}

/* ───────────────────────── history ───────────────────────── */
function snapshot() { return JSON.stringify({ geom: S.geom, enh: S.enh }); }
function pushHistory() {
    S.history.push(snapshot()); S.future = [];
    if (S.history.length > 60) S.history.shift();
    emit('history');
}
function restore(json) {
    var o = JSON.parse(json);
    S.geom = o.geom; S.enh = o.enh;
    emit('enh'); emit('geom');
    return rebuildBase();
}
function undo() { if (!S.history.length) return; S.future.push(snapshot()); restore(S.history.pop()); emit('history'); }
function redo() { if (!S.future.length) return; S.history.push(snapshot()); restore(S.future.pop()); emit('history'); }

/* ───────────────────────── source management ───────────────────────── */
function setSource(img, meta) {
    // a frame of the video replaces a fused picture: the window behind it goes
    if (S.track) { endTrack(); emit('track_end'); }
    S.source = img;
    S.sourceMeta = Object.assign({ time: 0, frame: 0, stacked: null }, meta || {});
    S.aiResult = null;
    // Geometry actions (crop/perspective) were taken on the previous picture.
    // Settings (deinterlace, aspect, lens) describe the CAMERA, so they carry
    // over: a DVR that weaves one frame weaves them all.
    S.geom.actions = [];
    dropRulerIf();
    checkComb(img);
    emit('source');
    return rebuildBase();
}
function checkComb(img) {
    var small = downscaleImageData(img, 960);
    work({ type: 'comb', imageData: cloneImageData(small) }).then(function (m) {
        S.combScore = m.score; emit('comb', { score: m.score, interlaced: !!m.interlaced });
    }).catch(function () {});
}

/* ───────────────────────── video source ───────────────────────── */
var video = null;
function bindVideo(el) { video = el; S.video.el = el; }
/* Frame n spans [n/fps, (n+1)/fps); we seek to its MIDDLE so the decoder
   cannot land on the boundary. floor(), not round(): with round() the middle
   of frame n reads back as n+1 and every arrow press skipped a frame. */
function frameOf(t) { return Math.max(0, Math.floor(t * S.video.fps + 1e-6)); }
function timeOfFrame(n) { return Math.min(S.video.duration, (n + 0.5) / S.video.fps); }
function seekPresented(t) {
    if (window.__seekPresented) return window.__seekPresented(video, t, 4000);
    return new Promise(function (res) {
        video.onseeked = function () { video.onseeked = null; res(); };
        try { video.currentTime = t; } catch (_) { res(); }
        setTimeout(res, 4000);
    });
}
function loadVideo(file) {
    return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var done = false;
        var watchdog = setTimeout(function () { if (!done) { done = true; reject(new Error('timeout')); } }, 20000);
        video.onloadedmetadata = function () {
            if (done) return;
            var settle = function () {
                if (done) return; done = true; clearTimeout(watchdog);
                /* A 0×0 picture is a decode failure, not a frame. MPEG-4 Part 2,
                   MJPEG, ProRes, DV and kin open with a duration, no size and no
                   error — and every frame read then threw «The source width is
                   0» (prod RU, Sept 2026: 27 times in 3 sessions), toast after
                   toast. Said once, as the "this browser cannot open it" block. */
                if (!video.videoWidth || !video.videoHeight) { reject(new Error('no_picture')); return; }
                S.video.loaded = true; S.video.duration = video.duration; S.video.w = video.videoWidth; S.video.h = video.videoHeight;
                resolve();
            };
            if (video.videoWidth && video.videoHeight) { settle(); return; }
            // some decoders report the size a moment after the metadata
            var wait = setTimeout(settle, 2500);
            video.addEventListener('resize', function once() { video.removeEventListener('resize', once); clearTimeout(wait); settle(); });
        };
        video.onerror = function () {
            if (done) return; done = true; clearTimeout(watchdog);
            reject(new Error('error_' + ((video.error && video.error.code) || 0)));
        };
        video.src = url;
        // fps from the container, not a guess: mp4box counts the samples.
        if (window.__videoProbe) {
            window.__videoProbe(file, { timeoutMs: 8000 }).then(function (r) {
                if (!r) return;
                if (r.fps && r.fps > 1 && r.fps < 240) { S.video.fps = r.fps; S.video.fpsExact = r.probe_path === 'mp4box'; }
                if (r.codec) S.video.codec = r.codec;
                if (file.size && r.duration) S.video.bitrate = Math.round(file.size * 8 / r.duration / 1000);
                emit('probe', r);
            }).catch(function () {});
        }
    });
}
function captureCurrent() {
    if (!S.video.loaded) return null;
    var c = document.createElement('canvas'); c.width = S.video.w; c.height = S.video.h;
    var x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(video, 0, 0);
    try {
        return x.getImageData(0, 0, c.width, c.height);
    } catch (e) {
        /* Chrome Android помечает <video> из blob: как cross-origin и не отдаёт
           пиксели — SecurityError «canvas has been tainted» (TIMBRICA-FRONTEND-HZ,
           Chrome Mobile 152 / Android 10, 07.09.2026). Кадр при этом не потерян:
           у mp4 он берётся из VideoFrame через WebCodecs-лейн, минуя элемент. */
        if (e && e.name === 'SecurityError') {
            if (!S.video.tainted) {
                S.video.tainted = true;
                try { window.__ae && window.__ae('feature_use', { slug: CFG.slug, f: 'capture_tainted', codec: S.video.codec || '' }); } catch (_) {}
            }
            var err = new Error('capture_tainted'); err.code = 'TAINTED'; throw err;
        }
        throw e;
    }
}
/* The decoded frame that <video> shows at t: the LAST one whose timestamp is
   ≤ t. Frame n starts at n/fps and the page parks the playhead in its middle,
   so "the first frame ≥ t" — what this code used to take — is always the
   NEXT frame, and "the nearest one" is a coin toss at exactly half a frame. */
function shownIndex(times, t) {
    var k = -1;
    for (var i = 0; i < times.length; i++) if (times[i] <= t + 1e-4) k = i;
    return k >= 0 ? k : 0;
}
function captureViaDecoder(t) {
    /* A window that starts a frame and a half early and ends a second late:
       the frame shown at t is then always among the first three decoded, and
       a decoder that holds frames in its reorder pipeline still has samples
       after them to push them out. */
    var got = null, gotTs = -1, first = null;
    var fps = S.video.fps || 30;
    var t0 = Math.max(0, t - 1.5 / fps), t1 = Math.min(S.video.duration || (t + 1), t + 1);
    return decodeRange(t0, t1, { maxFrames: 4, onFrame: function (img, i, ts) {
        if (!first) first = img;
        if (ts <= t + 1e-4 && ts >= gotTs) { got = img; gotTs = ts; }
    } }).then(function () {
        if (!got) got = first;
        if (!got) { var e = new Error('capture_tainted'); e.code = 'TAINTED'; throw e; }
        return got;
    });
}
function captureAt(t) {
    if (S.video.tainted) return captureViaDecoder(t);
    return seekPresented(t).then(function () {
        try { return captureCurrent(); }
        catch (e) { if (e && e.code === 'TAINTED') return captureViaDecoder(t); throw e; }
    });
}

/* ── "The N frames around this one" ──
   Fast lane: the site's one WebCodecs frame extractor in 'range' mode — it
   seeks to the keyframe before the window, reads presentation time through
   the file's edit list (the timeline <video> shows), climbs the decoder hint
   ladder and has stall watchdogs. Slow lane: seeking <video> frame by frame,
   for containers the extractor does not demux.

   Until 01.10.2026 this file carried its OWN decoder for the fast lane, and it
   closed the decoder the moment the demuxer saw a sample past the window —
   before a single queued frame had come out. Every window except the clip's
   last second came back empty (prod RU, Sept 2026: `range_decode_empty` in 56
   of the 222 sessions that opened a file, 169 times), so every sharpest-frame
   search and every stack quietly ran on the slow lane instead.

   opts.crop {x,y,w,h} (in <video> pixels) hands out just that region at full
   resolution — what the plate tracker keeps of each frame. */
function mp4Probe(file) {
    var ex = window.WebCodecsFrameExtractor;
    if (!ex || typeof ex.isSupported !== 'function' || !ex._isMp4Family(file)) return Promise.resolve(null);
    if (S.video.mp4 && S.video.mp4.file === file) return Promise.resolve(S.video.mp4.meta);
    return ex.isSupported(file).then(function (cap) {
        if (!cap || !cap.ok || !cap._meta) return null;
        var meta = cap._meta;
        S.video.mp4 = { file: file, meta: meta };
        if (meta.nbSamples && meta.durationSec > 0) {
            var fps = meta.nbSamples / meta.durationSec;
            if (fps > 1 && fps < 240) { S.video.fps = Math.round(fps * 1000) / 1000; S.video.fpsExact = true; S.video.nbFrames = meta.nbSamples; }
        }
        return meta;
    }).catch(function () { return null; });
}
/* Where the crop lands on a decoded frame. The frame may come out at a size
   other than videoWidth × videoHeight (a rotation tag, an odd coded size), so
   the box is scaled into the frame and delivered back at the asked size. */
function cropSource(crop, fw, fh) {
    var kx = fw / (S.video.w || fw), ky = fh / (S.video.h || fh);
    var sx = Math.max(0, Math.min(fw - 1, Math.round(crop.x * kx))), sy = Math.max(0, Math.min(fh - 1, Math.round(crop.y * ky)));
    return { sx: sx, sy: sy, sw: Math.max(1, Math.min(fw - sx, Math.round(crop.w * kx))), sh: Math.max(1, Math.min(fh - sy, Math.round(crop.h * ky))) };
}
function decodeRangeMp4(file, t0, t1, opts) {
    var ex = window.WebCodecsFrameExtractor, pr = S.video.mp4;
    var maxFrames = opts.maxFrames || 400, maxW = opts.maxWidth || 0, crop = opts.crop || null;
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    var canvas = document.createElement('canvas'), cx = null, emitted = 0, enough = false;
    return ex.extract(file, {
        mode: 'range', rangeStart: t0, rangeEnd: t1, format: 'bitmap',
        maxWidth: crop ? null : (maxW || null),
        _meta: pr && pr.meta, abortSignal: ac ? ac.signal : null,
        onFrame: function (bmp, _i, ts) {
            if (enough) { try { bmp.close(); } catch (_) {} return; }
            S.video.lastDecodeDims = bmp.width + 'x' + bmp.height;    // for a failure report: decoder frame vs <video>
            var src = crop ? cropSource(crop, bmp.width, bmp.height) : { sx: 0, sy: 0, sw: bmp.width, sh: bmp.height };
            var ow = crop ? crop.w : src.sw, oh = crop ? crop.h : src.sh;
            if (!crop && maxW && ow > maxW) { oh = Math.round(oh * maxW / ow); ow = Math.round(maxW); }
            if (canvas.width !== ow || canvas.height !== oh || !cx) { canvas.width = ow; canvas.height = oh; cx = canvas.getContext('2d', { willReadFrequently: true }); }
            cx.drawImage(bmp, src.sx, src.sy, src.sw, src.sh, 0, 0, ow, oh);
            try { bmp.close(); } catch (_) {}
            var img = cx.getImageData(0, 0, ow, oh), idx = emitted++;
            if (opts.onProgress) opts.onProgress(Math.min(1, (ts - t0) / Math.max(0.01, t1 - t0)));
            if (emitted >= maxFrames) { enough = true; if (ac) ac.abort(); }
            return opts.onFrame(img, idx, ts);
        }
    }).then(function () { return { emitted: emitted }; }, function (e) {
        if (enough && e && e.name === 'AbortError') return { emitted: emitted };
        throw e;
    });
}
/* Fallback: step <video> frame by frame. Slow (a seek per frame) but works
   for WebM / MKV / AVI the browser can play. */
function decodeRangeSeek(t0, t1, opts) {
    var step = 1 / S.video.fps, times = [];
    for (var t = t0; t <= t1 + 1e-6 && times.length < (opts.maxFrames || 400); t += step) times.push(t);
    var i = 0;
    var maxW = opts.maxWidth || 0, crop = opts.crop || null;
    function next() {
        if (i >= times.length) return Promise.resolve({ emitted: i, decoded: i });
        var t = times[i], idx = i++;
        return seekPresented(t).then(function () {
            var img = captureCurrent();
            if (crop) img = cropImageData(img, crop);
            else if (maxW && img.width > maxW) img = downscaleImageData(img, maxW);
            if (opts.onProgress) opts.onProgress(i / times.length);
            return Promise.resolve(opts.onFrame(img, idx, t));
        }).then(next);
    }
    return next();
}
function decodeRange(t0, t1, opts) {
    t0 = Math.max(0, t0); t1 = Math.min(S.video.duration, t1);
    if (t1 <= t0) return Promise.resolve({ emitted: 0 });
    S.video.lastLane = 'seek'; S.video.lastDecodeDims = '';
    return mp4Probe(S.file).then(function (meta) {
        if (meta) S.video.lastLane = 'mp4';
        if (meta) return decodeRangeMp4(S.file, t0, t1, opts).then(function (r) {
            /* RESOLVED IS NOT THE SAME AS WORKED. The demux lane finishes
               normally after emitting nothing at all — no throw, no message —
               and the caller then reports "could not stack" to the user while a
               working seek lane sits right here unused. Measured on a plain
               1280x720 H.264 clip: the fast lane emitted 0 frames anywhere
               except the last second, and /forensic-enhancer delivered a result
               in 5 of the 111 RU sessions that ever chose a file.
               Zero frames is a verdict about the LANE, not about the video. */
            if (r && r.emitted > 0) return r;
            S.video.lastLane = 'seek_after_empty';
            try { window.__ae && window.__ae('feature_use', { slug: CFG.slug, f: 'range_decode_empty', t0: Math.round(t0 * 100) / 100, t1: Math.round(t1 * 100) / 100 }); } catch (_) {}
            return decodeRangeSeek(t0, t1, opts);
        }).catch(function (e) {
            S.video.lastLane = 'seek_after_error';
            try { window.__ae && window.__ae('feature_use', { slug: CFG.slug, f: 'range_decode_fallback', reason: String(e && e.message).slice(0, 80) }); } catch (_) {}
            return decodeRangeSeek(t0, t1, opts);
        });
        return decodeRangeSeek(t0, t1, opts);
    });
}

/* ───────────────────────── sharpest frame ───────────────────────── */
function findSharpest(rangeSec, box) {
    var t = video.currentTime;
    var t0 = Math.max(0, t - rangeSec), t1 = Math.min(S.video.duration, t + rangeSec);
    var frames = [], times = [];
    busy(1, T.sharp_busy);
    var scale = 1;
    return decodeRange(t0, t1, {
        maxWidth: 640, maxFrames: 240,
        onFrame: function (img, idx, ts) { frames.push({ data: img.data, width: img.width, height: img.height }); times.push(ts); },
        onProgress: function (p) { emit('progress', { key: 'sharp', p: p * 0.7 }); }
    }).then(function () {
        if (!frames.length) return [];
        scale = frames[0].width / S.video.w;
        var scaledBox = box ? { x: box.x * scale, y: box.y * scale, w: box.w * scale, h: box.h * scale } : null;
        var transfer = frames.map(function (f) { return f.data.buffer; });
        return work({ type: 'sharpness', frames: frames, box: scaledBox }, transfer, function (p) { emit('progress', { key: 'sharp', p: 0.7 + p * 0.3 }); })
            .then(function (m) {
                var ranked = m.scores.map(function (s, i) { return { score: s, time: times[i], frame: frameOf(times[i]) }; });
                ranked.sort(function (a, b) { return b.score - a.score; });
                return ranked;
            });
    }).then(function (r) { busy(-1); return r; }, function (e) { busy(-1); throw e; });
}

/* ───────────────────────── stacking ───────────────────────── */
/* N frames centred on the current one, registered onto it, averaged (or
   median-combined). The result REPLACES the source: the picture is now the
   mean of N captures, and every geometry/enhancement step re-runs on it. */
function stackAround(n, mode, align) {
    if (!S.video.loaded) return Promise.reject(new Error('NO_VIDEO'));
    var fps = S.video.fps, tc = video.currentTime;
    var half = Math.floor(n / 2);
    /* SLIDE the window, do not clamp it. Centring on the playhead and then
       trimming against 0 / duration means a frame near either end collects a
       couple of frames and throws STACK_TOO_FEW — and the ends of a clip are
       exactly where a dashcam's useful moment tends to be. Take the same n
       frames from the nearest place they exist instead. */
    /* A frame and a half of slack on each side, then EXACTLY n frames are
       picked round the shown one below. The window counts frame STARTS (the
       frame extractor hands out presentation timestamps), so a window n frames
       long holds n or n − 1 of them depending on where the playhead sits in
       its frame — measured: 7 of 8 in the middle of a clip. */
    var span = (n + 3) / fps;
    var want0 = tc - (half + 1.5) / fps;
    var want1 = want0 + span;
    if (want0 < 0) { want1 -= want0; want0 = 0; }
    if (want1 > S.video.duration) {
        var over = want1 - S.video.duration;
        want1 = S.video.duration;
        want0 = Math.max(0, want0 - over);
    }
    var t0 = want0, t1 = want1;
    var frames = [], times = [];
    busy(1, T.stack_busy);
    return decodeRange(t0, t1, {
        maxFrames: n + 4,
        onFrame: function (img, idx, ts) { frames.push({ data: img.data, width: img.width, height: img.height }); times.push(ts); },
        onProgress: function (p) { emit('progress', { key: 'stack', p: p * 0.5 }); }
    }).then(function () {
        // exactly n frames round the shown one: half before, half after, the
        // rest from the side that has them (the ends of a clip)
        if (frames.length > n) {
            var sel = shownIndex(times, tc), lo = sel, hi = sel;
            while (hi - lo + 1 < n) {
                if (lo > 0 && (sel - lo <= hi - sel || hi >= frames.length - 1)) lo--;
                else if (hi < frames.length - 1) hi++;
                else break;
            }
            frames = frames.slice(lo, hi + 1); times = times.slice(lo, hi + 1);
        }
        if (frames.length < 2) {
            var tooFew = new Error('STACK_TOO_FEW');
            tooFew.code = 'TOO_FEW';
            tooFew.got = frames.length;
            tooFew.asked = n;
            throw tooFew;
        }
        // reference = the decoded frame the user is looking at
        var ref = shownIndex(times, tc);
        // Align inside the crop box when the only geometry action is a crop —
        // that box is the object the user cares about, and the background
        // would otherwise win the registration.
        var roi = null;
        var acts = S.geom.actions;
        if (align && acts.length === 1 && acts[0].type === 'crop' && !S.geom.rotate && S.geom.par === 1 && !S.geom.k1) roi = acts[0].rect;
        var transfer = frames.map(function (f) { return f.data.buffer; });
        return work({ type: 'stack', frames: frames, align: !!align, mode: mode, refIndex: ref, alignRoi: roi }, transfer, function (p) { emit('progress', { key: 'stack', p: 0.5 + p * 0.5 }); })
            .then(function (m) {
                var maxShift = 0;
                (m.offsets || []).forEach(function (o) { maxShift = Math.max(maxShift, Math.abs(o.dx), Math.abs(o.dy)); });
                var img = toImageData(m.imageData);
                var info = { n: frames.length, mode: m.mode || mode, aligned: !!align, maxShift: maxShift, roi: !!roi, time: times[ref] };
                var keepActions = S.geom.actions;
                pushHistory();
                S.source = img;
                S.sourceMeta = { time: times[ref], frame: frameOf(times[ref]), stacked: info };
                S.aiResult = null;
                S.geom.actions = keepActions;     // same picture geometry — the crop still applies
                emit('source');
                return rebuildBase().then(function () { return info; });
            });
    }).then(function (r) { busy(-1); return r; }, function (e) { busy(-1); throw e; });
}

/* ───────────────────────── tracked fusion ("собрать номер") ─────────────────────────
   The box the person drew is followed through the frames around this one by
   track-worker.js (its own worker: a 120-frame window lives there, and
   terminating it is how that memory comes back), every frame gets its own
   perspective warp, and the frames are fused on a finer grid. See the
   worker's header for the method and why a whole-frame shift cannot do this.

   Only a CORRIDOR around the box is kept of each frame: the plate cannot
   leave it in a couple of seconds without leaving the useful frames too, and
   a 1080p window of 120 full frames would not fit a phone. */
var trackW = null, trackSeq = 0, trackPending = new Map(), trackReq = 0;
function trackWorker() {
    if (trackW) return trackW;
    trackW = new Worker(CFG.trackWorker || '/js/forensic-enhancer/track-worker.js');
    trackW.onmessage = function (e) {
        var m = e.data; if (!m || m.id == null) return;
        var p = trackPending.get(m.id); if (!p) return;
        if (m.type === 'progress') { if (p.onProgress) p.onProgress(m.percent / 100); return; }
        trackPending.delete(m.id);
        if (m.type === 'error') { var err = new Error(m.message || m.code || 'TRACK_FAILED'); err.code = m.code; err.got = m.got; err.n = m.n; err.diag = m.diag; p.reject(err); }
        else p.resolve(m);
    };
    trackW.onerror = function (e) {
        trackPending.forEach(function (p) { p.reject(new Error('WORKER_CRASHED')); });
        trackPending.clear();
        try { trackW.terminate(); } catch (_) {}
        trackW = null; S.track = null;
        try { console.error('track-worker', e); } catch (_) {}
    };
    return trackW;
}
function twork(msg, transfer, onProgress) {
    return new Promise(function (resolve, reject) {
        var w = trackWorker();
        msg.id = ++trackReq;
        trackPending.set(msg.id, { resolve: resolve, reject: reject, onProgress: onProgress });
        if (transfer && transfer.length) w.postMessage(msg, transfer); else w.postMessage(msg);
    });
}
function endTrack() {
    if (trackW) { try { trackW.terminate(); } catch (_) {} trackW = null; }
    trackPending.forEach(function (p) { p.reject(new Error('TRACK_ENDED')); });
    trackPending.clear();
    S.track = null;
}
function corridorFor(box, W, H) {
    var cw = Math.min(W, Math.max(box.w + 64, Math.round(box.w * 5 + W * 0.25)));
    var ch = Math.min(H, Math.max(box.h + 64, Math.round(box.h * 8 + H * 0.25)));
    var cx = Math.round(box.x + box.w / 2 - cw / 2), cy = Math.round(box.y + box.h / 2 - ch / 2);
    return { x: Math.max(0, Math.min(W - cw, cx)), y: Math.max(0, Math.min(H - ch, cy)), w: cw, h: ch };
}
/* How many frames the window may hold: the time asked for, but never more
   luma than the device can spare (the corridor costs w×h bytes a frame).
   The device's memory is the layout's answer (__memBudget): an iPhone tab
   gets the small window whatever its RAM. */
function trackFrameBudget(cor, wantFrames) {
    var B = window.__memBudget;
    var mem = B ? B.gb() : 2;
    var bytes = (mem <= 2 || (B && B.constrained()) ? 32 : mem <= 4 ? 64 : 160) * 1048576;
    return Math.max(3, Math.min(wantFrames, 241, Math.floor(bytes / Math.max(1, cor.w * cor.h))));
}
function trackFuse(box, opts) {
    if (!S.video.loaded) return Promise.reject(new Error('NO_VIDEO'));
    opts = opts || {};
    var range = opts.range || 1, K = opts.K || 3, fps = S.video.fps || 30;
    var tc = S.sourceMeta.time || 0;
    var cor = corridorFor(box, S.video.w, S.video.h);
    var t0 = Math.max(0, tc - range), t1 = Math.min(S.video.duration, tc + range);
    var maxFrames = trackFrameBudget(cor, Math.ceil((t1 - t0) * fps) + 2);
    if (maxFrames < Math.ceil((t1 - t0) * fps) + 2) {
        // the budget is shorter than the window: centre the frames we can keep
        var half = (maxFrames - 1) / 2 / fps;
        t0 = Math.max(0, tc - half); t1 = Math.min(S.video.duration, tc + half);
    }
    var session = ++trackSeq, times = [], started = performance.now(), decodeMs = 0;
    var refCrop = cropImageData(S.source, box);     // the frame as the camera gave it, for the report
    endTrack();
    busy(1, T.track_busy);
    return twork({ type: 'begin', session: session, w: cor.w, h: cor.h }).then(function () {
        return decodeRange(t0, t1, {
            crop: cor, maxFrames: maxFrames,
            onFrame: function (img, idx, ts) {
                times.push(ts);
                return twork({ type: 'frame', session: session, idx: idx, ts: ts, data: img.data, w: img.width, h: img.height }, [img.data.buffer]);
            },
            onProgress: function (p) { emit('progress', { key: 'track', p: p * 0.45 }); }
        });
    }).then(function () {
        decodeMs = Math.round(performance.now() - started);
        if (times.length < 3) { var e = new Error('TOO_FEW'); e.code = 'TOO_FEW'; e.got = times.length; e.n = times.length; throw e; }
        return twork({ type: 'run', session: session, refIdx: shownIndex(times, tc), box: { x: box.x - cor.x, y: box.y - cor.y, w: box.w, h: box.h }, K: K, rhoUse: 0.75 },
            null, function (p) { emit('progress', { key: 'track', p: 0.45 + p * 0.55 }); });
    }).then(function (m) {
        S.track = { session: session, box: box, corridor: cor, K: m.stats.K, tc: tc, range: range, refCrop: refCrop };
        return installTracked(m, { ms: Math.round(performance.now() - started), msDecode: decodeMs });
    }).then(function (r) { busy(-1); return r; }, function (e) { busy(-1); throw e; });
}
/* Re-fuse the same track with a different set of frames or scale — no
   decoding, no tracking: the worker kept the window. */
function trackRefuse(use, K) {
    if (!S.track || !trackW) return Promise.reject(new Error('NO_TRACK'));
    busy(1, T.track_busy);
    return twork({ type: 'fuse', session: S.track.session, use: use, K: K || S.track.K }, null, function (p) { emit('progress', { key: 'track', p: p }); })
        .then(function (m) { S.track.K = m.stats.K; return installTracked(m, {}); })
        .then(function (r) { busy(-1); return r; }, function (e) { busy(-1); throw e; });
}
function installTracked(m, extra) {
    var img = new ImageData(m.image.data, m.image.width, m.image.height);
    var before = new ImageData(m.before.data, m.before.width, m.before.height);
    var st = m.stats, tr = S.track;
    var info = Object.assign({}, st, { box: tr.box, range: tr.range, time: tr.tc, frames: m.frames }, extra || {});
    pushHistory();
    S.source = img;
    S.sourceMeta = { time: tr.tc, frame: frameOf(tr.tc), stacked: null, tracked: info, compareWith: before, refCrop: tr.refCrop };
    S.aiResult = null;
    S.geom.actions = [];                 // a new picture: nothing taken on the old one applies
    dropRulerIf();
    emit('source');
    return rebuildBase().then(function () { return info; });
}

/* ───────────────────────── plate reader ─────────────────────────
   A Russian plate of the common type (ГОСТ Р 50577, type 1: А 123 ВС 77) read
   by a small network in a worker of its own (plate-reader-worker.js). It
   answers with a LIST — the likeliest plates with probabilities and, for each
   position, the likeliest symbols — because at the widths this tool works at
   a single answer would be a guess dressed up as a fact.

   The network gets what it was trained on, never the picture on the screen:
   the person's box on ONE frame (the reader enlarges it ×3 itself), or the
   tracker's fused picture as it came out (the reader deconvolves it, σ = K/2,
   as the tool's optical step does). Contrast, sharpening and the rest of the
   chain are for eyes; they move the input away from what the network knows. */
var plateW = null, plateReq = 0, platePending = new Map(), plateReady = null;
function plateWorker() {
    if (plateW) return plateW;
    plateW = new Worker(CFG.plateWorker || '/js/forensic-enhancer/plate-reader-worker.js');
    plateW.onmessage = function (e) {
        var m = e.data; if (!m || m.id == null) return;
        var p = platePending.get(m.id); if (!p) return;
        if (m.type === 'progress') { if (p.onProgress) p.onProgress(m.percent / 100); return; }
        platePending.delete(m.id);
        if (m.type === 'error') { var err = new Error(m.message || m.code || 'PLATE_FAILED'); err.code = m.code; p.reject(err); }
        else p.resolve(m);
    };
    plateW.onerror = function (e) {
        platePending.forEach(function (p) { p.reject(Object.assign(new Error('WORKER_CRASHED'), { code: 'WORKER_CRASHED' })); });
        platePending.clear();
        try { plateW.terminate(); } catch (_) {}
        plateW = null; plateReady = null;
        try { console.error('plate-reader', e); } catch (_) {}
    };
    return plateW;
}
function pcall(msg, onProgress) {
    return new Promise(function (resolve, reject) {
        var w = plateWorker();
        msg.id = ++plateReq;
        platePending.set(msg.id, { resolve: resolve, reject: reject, onProgress: onProgress });
        w.postMessage(msg);
    });
}
function plateInit() {
    if (plateReady) return plateReady;
    var B = window.__ortBuild;
    if (!B || !CFG.plateModel) return Promise.reject(Object.assign(new Error('ORT_UNAVAILABLE'), { code: 'ORT_UNAVAILABLE' }));
    // the CPU build: one small network in a worker, no use for WebGPU (__ortBuild, cpuOnly)
    plateReady = pcall({ type: 'init', ortUrl: B.url({ cpuOnly: true }), wasmBase: B.wasmBase(), modelUrl: CFG.plateModel, modelFallbackUrl: CFG.plateModelFallback })
        .catch(function (e) { plateReady = null; throw e; });
    return plateReady;
}
/* BT.601 luma of a rectangle (the whole picture when r is absent) — what the
   training photographs were turned grey with. */
function lumaOf(img, r) {
    r = r || { x: 0, y: 0, w: img.width, h: img.height };
    var x0 = Math.max(0, Math.round(r.x)), y0 = Math.max(0, Math.round(r.y));
    var w = Math.max(0, Math.min(img.width - x0, Math.round(r.w))), h = Math.max(0, Math.min(img.height - y0, Math.round(r.h)));
    var out = new Uint8Array(w * h), d = img.data;
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
        var i = ((y0 + y) * img.width + x0 + x) * 4;
        out[y * w + x] = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
    }
    return { data: out, w: w, h: h };
}
/* The box a plate is read through: its corners' bounding box with a margin of plate round the
   characters — what a careful person draws, and what the network was measured on (a box round
   the plate, ×3 by the reader). */
function plateReadBox(q, W, H) {
    var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    q.forEach(function (p) { x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]); x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1]); });
    var mx = (x1 - x0) * 0.06, my = (y1 - y0) * 0.2;
    var x = Math.max(0, Math.floor(x0 - mx)), y = Math.max(0, Math.floor(y0 - my));
    return { x: x, y: y, w: Math.min(W - x, Math.ceil(x1 - x0 + 2 * mx)), h: Math.min(H - y, Math.ceil(y1 - y0 + 2 * my)) };
}
/* target: {fused:true} — the fused picture IS the plate; {plate} — a plate found on the picture
   (base coordinates); {box} — the person's box on the picture shown. A bare box (the old call)
   is taken as {box}. */
function plateRead(target, onProgress) {
    var tr = S.sourceMeta.tracked, inp;
    if (target && target.x != null) target = { box: target };
    target = target || {};
    if (tr && S.source) {
        var K = tr.K || 3, f = lumaOf(S.source);
        inp = { mode: 'fused', bw: tr.box.w, images: [{ data: f.data, w: f.w, h: f.h, rl: 0.5 * K }] };
    } else {
        var box = target.plate && S.base ? plateReadBox(target.plate.quad, S.base.width, S.base.height) : target.box;
        if (!S.base || !box || box.w < 8 || box.h < 4) return Promise.reject(Object.assign(new Error('NO_BOX'), { code: 'NO_BOX' }));
        var g = lumaOf(S.base, box);
        inp = { mode: 'single', bw: target.plate ? target.plate.w : g.w, eff: target.plate ? target.plate.eff : null, images: [{ data: g.data, w: g.w, h: g.h, up: 3 }] };
    }
    var t0 = performance.now(), msInit = 0;
    return plateInit().then(function () {
        msInit = Math.round(performance.now() - t0);
        return pcall({ type: 'read', images: inp.images, k: 10, temp: (CFG.plateTemp || {})[inp.mode] || 1 }, onProgress);
    }).then(function (r) {
        r.mode = inp.mode; r.bw = inp.bw; r.eff = inp.eff; r.n = inp.images.length; r.msInit = msInit; r.msAll = Math.round(performance.now() - t0);
        return r;
    });
}

/* ───────────────────────── a plate of another country ─────────────────────────
   fast-plate-ocr v2 «global» (MIT) in its own worker (plate-reader-intl-worker.js): ten
   positions over 0–9 A–Z and the plate's country. Its input is the plate STRAIGHTENED from the
   four corners the detector found (a box drawn by hand is taken as it is), at the picture's own
   resolution — the worker resizes to what the network was trained on. */
var intlW = null, intlReq = 0, intlPending = new Map(), intlReady = {};
/* The readers this worker can hold, by key: 'global' (65 regions) is always there; the page's
   config may add others (CFG.plateIntlModels). */
function intlModel(key) {
    var all = CFG.plateIntlModels || {};
    if (all[key]) return all[key];
    if (key === 'global' && CFG.plateIntlModel) return { url: CFG.plateIntlModel, fallback: CFG.plateIntlModelFallback, temp: CFG.plateIntlTemp, regions: CFG.plateIntlRegions };
    return null;
}
function intlWorker() {
    if (intlW) return intlW;
    intlW = new Worker(CFG.plateIntlWorker || '/js/forensic-enhancer/plate-reader-intl-worker.js');
    intlW.onmessage = function (e) {
        var m = e.data; if (!m || m.id == null) return;
        var p = intlPending.get(m.id); if (!p) return;
        intlPending.delete(m.id);
        if (m.type === 'error') { var err = new Error(m.message || m.code || 'PLATE_FAILED'); err.code = m.code; p.reject(err); }
        else p.resolve(m);
    };
    intlW.onerror = function (e) {
        intlPending.forEach(function (p) { p.reject(Object.assign(new Error('WORKER_CRASHED'), { code: 'WORKER_CRASHED' })); });
        intlPending.clear();
        try { intlW.terminate(); } catch (_) {}
        intlW = null; intlReady = {};
        try { console.error('plate-reader-intl', e); } catch (_) {}
    };
    return intlW;
}
function icall(msg, transfer) {
    return new Promise(function (resolve, reject) {
        var w = intlWorker();
        msg.id = ++intlReq;
        intlPending.set(msg.id, { resolve: resolve, reject: reject });
        if (transfer && transfer.length) w.postMessage(msg, transfer); else w.postMessage(msg);
    });
}
function intlInit(key) {
    key = key || 'global';
    if (intlReady[key]) return intlReady[key];
    var B = window.__ortBuild, md = intlModel(key);
    if (!B || !md) return Promise.reject(Object.assign(new Error('ORT_UNAVAILABLE'), { code: 'ORT_UNAVAILABLE' }));
    intlReady[key] = icall({ type: 'init', key: key, ortUrl: B.url({ cpuOnly: true }), wasmBase: B.wasmBase(), modelUrl: md.url, modelFallbackUrl: md.fallback, regions: md.regions || null })
        .catch(function (e) { intlReady[key] = null; throw e; });
    return intlReady[key];
}
/* keys: one reader ('global') or several (['global', 'eubr']) — the plate picture is prepared once
   and read by each; several keys answer an array in the same order. */
function plateReadIntl(target, onProgress, keys) {
    var many = Array.isArray(keys), list = many ? keys : [keys || 'global'];
    var tr = S.sourceMeta.tracked, img, mode, bw, eff = null;
    target = target || {};
    if (tr && S.source) {
        // the fused plate as the tool shows it: its deconvolution (σ = K/2) on, nothing else
        mode = 'fused'; bw = tr.box.w;
        var K = tr.K || 3, src = cloneImageData(S.source);
        img = work({ type: 'pipeline', imageData: src, params: { filters: [{ name: 'optical', enabled: true, params: { sigma: 0.5 * K, iters: 10 } }] } }, [src.data.buffer])
            .then(function (m) { return toImageData(m.imageData); });
    } else if (target.plate && S.base && window.FXPlates) {
        mode = 'single'; bw = target.plate.w; eff = target.plate.eff;
        var mg = CFG.plateIntlMargin == null ? 0.06 : CFG.plateIntlMargin;
        img = Promise.resolve(window.FXPlates.rectifyRGBA(S.base, target.plate.quad, { margin: mg, marginY: mg * 1.6 }));
    } else if (target.box && S.base && target.box.w >= 8 && target.box.h >= 4) {
        mode = 'single'; bw = target.box.w;
        img = Promise.resolve(cropImageData(S.base, target.box));
    } else return Promise.reject(Object.assign(new Error('NO_BOX'), { code: 'NO_BOX' }));
    var t0 = performance.now(), msInit = 0;
    return Promise.all(list.map(intlInit)).then(function () {
        msInit = Math.round(performance.now() - t0);
        if (onProgress) onProgress(0.4);
        return img;
    }).then(function (im) {
        var out = [];
        return list.reduce(function (chain, key, i) {
            return chain.then(function () {
                var md = intlModel(key) || {}, d = new Uint8ClampedArray(im.data);
                return icall({ type: 'read', key: key, images: [{ data: d, w: im.width, h: im.height }], k: 10, temp: (md.temp || {})[mode] || 1 }, [d.buffer]).then(function (r) {
                    r.mode = mode; r.bw = bw; r.eff = eff; r.n = 1; r.msInit = msInit; r.reader = key;
                    out.push(r);
                    if (onProgress) onProgress(0.4 + 0.6 * (i + 1) / list.length);
                });
            });
        }, Promise.resolve()).then(function () {
            var all = Math.round(performance.now() - t0);
            out.forEach(function (r) { r.msAll = all; });
            return many ? out : out[0];
        });
    });
}

/* ───────────────────────── daily cap ─────────────────────────
   One unit per SOURCE FILE, spent when a result is DELIVERED: a frame or a
   report downloaded, or a multi-frame result (stack, plate fusion) put on the
   screen — for this tool that picture is the result: a plate read off the
   screen never needs a download. A second result of the same file the same
   day costs nothing (the key is the file, stamped in openFile). */
function bill() {
    if (!window.__toolLimit) return { commit: function () {}, abort: function () {} };
    return window.__toolLimit.begin(CFG.slug, S.limitKey ? [S.limitKey] : null) || null;
}

/* ───────────────────────── captured frames strip ───────────────────────── */
var capId = 0;
function addCaptured(img, time, frame, extra) {
    var thumbC = document.createElement('canvas');
    var tw = 144, th = Math.max(1, Math.round(img.height * tw / img.width));
    thumbC.width = tw; thumbC.height = th;
    thumbC.getContext('2d').drawImage(imageDataToCanvas(img), 0, 0, tw, th);
    var entry = { id: ++capId, w: img.width, h: img.height, time: time, frame: frame, selected: false, thumb: thumbC.toDataURL('image/jpeg', 0.75), best: !!(extra && extra.best), bmp: null };
    return createImageBitmap(img).then(function (bmp) { entry.bmp = bmp; S.captured.push(entry); emit('captured'); return entry; });
}
function capturedImageData(entry) { return drawableToImageData(entry.bmp, entry.w, entry.h); }
function removeCaptured(id) {
    S.captured = S.captured.filter(function (c) { if (c.id === id) { try { c.bmp.close(); } catch (_) {} return false; } return true; });
    emit('captured');
}
function clearCaptured() { S.captured.forEach(function (c) { try { c.bmp.close(); } catch (_) {} }); S.captured = []; emit('captured'); }

/* Batch: geometry + enhancement of the live picture, applied to every
   selected captured frame. Perspective/crop actions are applied as taken
   (they are coordinates, and the frames share the camera). */
function batchApply(onProgress) {
    var sel = S.captured.filter(function (c) { return c.selected; });
    if (!sel.length) return Promise.resolve(0);
    busy(1, T.batch_busy);
    var i = 0;
    function next() {
        if (i >= sel.length) return Promise.resolve(sel.length);
        var c = sel[i++];
        var img = capturedImageData(c);
        return geometryOn(img).then(function (b) { return runChain(b, S.enh); }).then(function (out) {
            return createImageBitmap(out).then(function (bmp) {
                try { c.bmp.close(); } catch (_) {}
                c.bmp = bmp; c.w = out.width; c.h = out.height;
                var tc = document.createElement('canvas'); tc.width = 144; tc.height = Math.max(1, Math.round(out.height * 144 / out.width));
                tc.getContext('2d').drawImage(bmp, 0, 0, tc.width, tc.height); c.thumb = tc.toDataURL('image/jpeg', 0.75);
                c.processed = true;
                if (onProgress) onProgress(i / sel.length);
                emit('captured');
            });
        }).then(next);
    }
    return next().then(function (n) { busy(-1); return n; }, function (e) { busy(-1); throw e; });
}
function geometryOn(img) {
    var g = S.geom;
    var p = Promise.resolve(cloneImageData(img));
    if (g.deint !== 'off') p = p.then(function (im) { return work({ type: 'pipeline', imageData: im, params: { filters: [{ name: 'deinterlace', enabled: true, params: { mode: g.deint } }] } }, [im.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    if (g.k1) p = p.then(function (im) { return work({ type: 'pipeline', imageData: im, params: { filters: [{ name: 'undistort', enabled: true, params: { k1: g.k1 } }] } }, [im.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    if (g.rotate) p = p.then(function (im) { return work({ type: 'rotate', imageData: im, quarter: g.rotate }, [im.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    if (g.par !== 1) p = p.then(function (im) { return stretchImageData(im, g.par); });
    g.actions.forEach(function (a) {
        if (a.type === 'crop') p = p.then(function (im) { return cropImageData(im, a.rect); });
        else if (a.type === 'persp') p = p.then(function (im) { return work({ type: 'warp', imageData: im, quad: a.quad, outWidth: a.outW, outHeight: a.outH }, [im.data.buffer]).then(function (m) { return toImageData(m.imageData); }); });
    });
    return p;
}

/* ───────────────────────── analysis (authenticity) ───────────────────────── */
function runAnalysis(algo, params) {
    if (!S.current) return Promise.reject(new Error('NO_FRAME'));
    var src = composite();
    if (algo === 'ela') {
        // Re-save as JPEG at 95% and diff: edits re-compressed at another
        // quality light up against the rest of the picture.
        return new Promise(function (resolve, reject) {
            var c = imageDataToCanvas(src);
            c.toBlob(function (blob) {
                if (!blob) return reject(new Error('ELA_ENCODE'));
                var im = new Image();
                im.onload = function () {
                    var x = c.getContext('2d', { willReadFrequently: true }); x.clearRect(0, 0, c.width, c.height); x.drawImage(im, 0, 0);
                    var resaved = x.getImageData(0, 0, c.width, c.height);
                    URL.revokeObjectURL(im.src);
                    work({ type: 'analysis', imageData: cloneImageData(src), params: { algorithm: 'ela', resavedData: resaved.data, amplification: 15 } }).then(resolve, reject);
                };
                im.onerror = function () { reject(new Error('ELA_DECODE')); };
                im.src = URL.createObjectURL(blob);
            }, 'image/jpeg', 0.95);
        });
    }
    return work({ type: 'analysis', imageData: cloneImageData(src), params: Object.assign({ algorithm: algo }, params || {}) });
}

/* ───────────────────────── AI reconstruction ───────────────────────── */
var aiCore = null;
/* region {x,y,w,h} (picture pixels): enlarge just that — the plate or the box — instead of the
   whole frame. A 1080p frame at ×4 is 33 Mpx and was refused outright («crop first», 10 sessions
   Sept–Oct 2026) although the person had already pointed at what they wanted to see. */
function aiUpscale(scale, onProgress, region) {
    var src = composite();
    if (!src) return Promise.reject(new Error('NO_FRAME'));
    if (region && region.w >= 8 && region.h >= 6) src = cropImageData(src, region);
    if (typeof window.AIUpscaleCore !== 'function') return Promise.reject(new Error('AI_UNSUPPORTED'));
    var caps = window.AIUpscaleCore.checkCapabilities ? window.AIUpscaleCore.checkCapabilities() : { supported: true };
    if (caps && caps.supported === false) return Promise.reject(new Error('AI_UNSUPPORTED'));
    var feed = src;
    // The model is ×4. For ×2 we feed the picture as-is and downscale the
    // result; for ×4 we feed as-is. Output pixels are capped so a full 1080p
    // frame at ×4 (33 Mpx) does not take the tab down — crop to the region first.
    var outPx = src.width * 4 * src.height * 4;
    var budget = (window.AIUpscaleShared && window.AIUpscaleShared.outputPixelBudget) ? window.AIUpscaleShared.outputPixelBudget() : 24e6;
    if (outPx > budget) return Promise.reject(new Error('AI_TOO_BIG'));
    var urls = [CFG.aiModel, CFG.aiModelFallback].filter(Boolean);
    busy(1, T.ai_busy);
    var p = Promise.resolve();
    if (!aiCore) {
        aiCore = new window.AIUpscaleCore({ modelUrls: urls, slug: CFG.slug });
        p = aiCore.init({ onProgress: function (pr) {
            if (pr.phase === 'download' && pr.total) onProgress && onProgress({ phase: 'model', p: (pr.loaded / pr.total) * 0.7 });
            else if (pr.phase === 'compile' || pr.phase === 'verify') onProgress && onProgress({ phase: 'model', p: 0.75 });
        } }).catch(function (e) { aiCore = null; throw e; });
    }
    return p.then(function () {
        return aiCore.upscaleFrame(imageDataToCanvas(feed), { onTileProgress: function (pr) { var f = typeof pr === 'number' ? pr : (pr && pr.done && pr.total ? pr.done / pr.total : 0); onProgress && onProgress({ phase: 'infer', p: 0.75 + 0.25 * f }); } });
    }).then(function (outCanvas) {
        var ow = outCanvas.width, oh = outCanvas.height;
        if (scale === 2) { ow = Math.round(outCanvas.width / 2); oh = Math.round(outCanvas.height / 2); }
        var img = drawableToImageData(outCanvas, ow, oh);
        S.aiResult = { img: img, scale: scale };
        emit('current');
        busy(-1);
        return img;
    }, function (e) { busy(-1); throw e; });
}
function aiRevert() { if (!S.aiResult) return; S.aiResult = null; emit('current'); }

/* ───────────────────────── reset ───────────────────────── */
function reset() {
    geomToken++;
    S.kind = null; S.file = null; S.original = null; S.rescueTier = ''; S.limitKey = null;
    S.video.loaded = false; S.video.duration = 0; S.video.fps = 30; S.video.fpsExact = false; S.video.w = S.video.h = 0; S.video.codec = ''; S.video.nbFrames = 0; S.video.mp4 = null; S.video.bitrate = 0;
    if (video && video.src) { try { URL.revokeObjectURL(video.src); } catch (_) {} video.removeAttribute('src'); try { video.load(); } catch (_) {} }
    S.source = S.base = S.current = null; S.aiResult = null;
    S.sourceMeta = { time: 0, frame: 0, stacked: null };
    S.geom = defaultGeom(); S.enh = defaultEnh();
    S.history = []; S.future = []; S.combScore = null; S.rulerScale = null;
    endTrack();
    clearCaptured();
    emit('reset');
}

window.FX = {
    S: S, T: T, CFG: CFG,
    CHAIN_ORDER: CHAIN_ORDER,
    on: on, emit: emit, work: work,
    cloneImageData: cloneImageData, imageDataToCanvas: imageDataToCanvas, drawableToImageData: drawableToImageData, cropImageData: cropImageData, downscaleImageData: downscaleImageData,
    setSource: setSource, rebuildBase: rebuildBase, composite: composite,
    setGeomSetting: setGeomSetting, addGeomAction: addGeomAction, resetGeometry: resetGeometry,
    setStep: setStep, setStrength: setStrength, resetEnhance: resetEnhance, applyPreset: applyPreset, applyEnhance: applyEnhance, scheduleEnhance: scheduleEnhance, runChain: runChain, geometryOn: geometryOn, chainFilters: chainFilters,
    searchDeblur: searchDeblur,
    pushHistory: pushHistory, undo: undo, redo: redo,
    bindVideo: bindVideo, loadVideo: loadVideo, captureCurrent: captureCurrent, captureAt: captureAt, seekPresented: seekPresented, frameOf: frameOf, timeOfFrame: timeOfFrame, decodeRange: decodeRange, mp4Probe: mp4Probe,
    findSharpest: findSharpest, stackAround: stackAround,
    trackFuse: trackFuse, trackRefuse: trackRefuse, endTrack: endTrack, corridorFor: corridorFor, shownIndex: shownIndex, bill: bill,
    plateRead: plateRead, plateReadIntl: plateReadIntl, plateReadBox: plateReadBox,
    addCaptured: addCaptured, capturedImageData: capturedImageData, removeCaptured: removeCaptured, clearCaptured: clearCaptured, batchApply: batchApply,
    runAnalysis: runAnalysis, aiUpscale: aiUpscale, aiRevert: aiRevert,
    reset: reset, busy: busy,
};
})();
